import { useEffect, useRef } from "react";

const ACTIVITY = ["pointerdown", "keydown", "touchstart", "wheel"] as const;

/**
 * Auto-lock (design/08 §1, T07-22): `onIdle` after `timeoutMs` without a touch / key; every touch
 * also calls `onActivity` (the staff token is renewed quietly while people work).
 */
export function useIdleLock(options: {
  enabled: boolean;
  timeoutMs: number;
  onIdle: () => void;
  onActivity?: () => void;
}): void {
  const { enabled, timeoutMs, onIdle, onActivity } = options;
  const idle = useRef(onIdle);
  const active = useRef(onActivity);
  useEffect(() => {
    idle.current = onIdle;
    active.current = onActivity;
  });

  useEffect(() => {
    if (!enabled || timeoutMs <= 0) {
      return;
    }
    let timer = setTimeout(() => idle.current(), timeoutMs);
    let last = 0;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => idle.current(), timeoutMs);
      const now = Date.now();
      if (now - last > 5_000) {
        last = now;
        active.current?.();
      }
    };
    for (const type of ACTIVITY) {
      window.addEventListener(type, reset, { passive: true, capture: true });
    }
    return () => {
      clearTimeout(timer);
      for (const type of ACTIVITY) {
        window.removeEventListener(type, reset, { capture: true });
      }
    };
  }, [enabled, timeoutMs]);
}
